import styled from '@emotion/styled';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  KeyValuePairSystemStringICollectionDtoV1BoltAuditEntityFieldResponse,
  KeyValuePairSystemStringICollectionDtoV1BoltAuditFieldAudit,
} from '~/__generated__/api';
import AccordionContainer from '~/common/components/AccordionContainer';
import TabPanel from '~/common/components/Tab/TabPanel';
import AuditHistoryAccordion from '~/pages/AuditHistory/DateTrackingAuditHistoryAccordion';
import palette from '~/theme/mui/palette';

import AuditTable from './AuditTable';
import Layout from './Layout';
import { AuditHistoryPageEnum } from './constants';
import { useGetAuditHistoryDynamicTabs } from './hooks';

const StyledDeletedByText = styled.div`
  display: flex;
  justify-content: flex-end;
  font-size: 0.75rem;
  letter-spacing: -0.12px;
  line-height: 1rem;
  padding-bottom: 0.5rem;
  span {
    font-weight: 500;
    padding-right: 0.25rem;
    letter-spacing: normal;
  }
`;

type AuditHistoryDynamicTabsParams = {
  category?: string;
  pageEnum?: string;
};

type AuditHistoryDynamicTabsProps = {
  page: string;
  pageEnum?: string;
};

type NormalizedAuditEntity = {
  sectionTitle: string;
  entitySectionTitle?: string;
  entityFields:
    | KeyValuePairSystemStringICollectionDtoV1BoltAuditFieldAudit[]
    | KeyValuePairSystemStringICollectionDtoV1BoltAuditEntityFieldResponse[];
  useAccordians?: boolean;
  entityDisplayName?: string;
  deletedByDisplayName?: string;
  tab?: string;
};

function TabPanelContent(
  auditEntities: {
    tab?: string;
    sectionTitle: string;
    entityFields: any[];
    entityDisplayName?: string;
    deletedByDisplayName?: string;
    useAccordions?: boolean;
    isActiveDeletedTab?: boolean;
  }[],
  selectedTab: number,
  tabNames: string[],
  groupedEntities: Record<string, NormalizedAuditEntity[]>,
) {
  if (auditEntities[selectedTab]?.isActiveDeletedTab) {
    return groupedEntities[tabNames[selectedTab]].map((entity, idx) => (
      <ActiveDeletedTabs
        key={`activeDeletedTabs-${tabNames[selectedTab]}-${entity.entityDisplayName}}`}
        entity={entity}
        idx={idx}
        selectedTab={selectedTab}
      />
    ));
  }

  return (
    <SingleTabData
      auditEntities={auditEntities}
      tabNames={tabNames}
      groupedEntities={groupedEntities}
      selectedTab={selectedTab}
      useAccordions={auditEntities[selectedTab]?.useAccordions}
    />
  );
}

function ActiveDeletedTabs({ entity, idx, selectedTab }) {
  if (entity.deletedByDisplayName) {
    return (
      <section key={`deletedEntities-${idx}`}>
        <StyledDeletedByText>
          <span>Deleted By:</span>
          {entity.deletedByDisplayName}
        </StyledDeletedByText>
        <AuditHistoryAccordion entity={entity} />
      </section>
    );
  } else {
    return (
      <AuditHistoryAccordion
        entity={entity}
        key={`activeEntities-${selectedTab}-${idx}`}
      />
    );
  }
}

function SingleTabData({
  auditEntities,
  selectedTab,
  useAccordions,
  tabNames,
  groupedEntities,
}) {
  if (useAccordions) {
    const entitiesForSelectedTab = groupedEntities[tabNames[selectedTab]] || [];
    return (
      <MultiTableAccordion entitiesForSelectedTab={entitiesForSelectedTab} />
    );
  }

  return (
    <AuditTable
      data={auditEntities[selectedTab]?.entityFields}
      section={{}}
      key={auditEntities[selectedTab]?.sectionTitle}
    />
  );
}

function MultiTableAccordion({
  entitiesForSelectedTab,
}: Readonly<{
  entitiesForSelectedTab: NormalizedAuditEntity[];
}>) {
  // Group entities by entityDisplayName
  const entitiesByDisplayName: Record<string, NormalizedAuditEntity[]> =
    entitiesForSelectedTab.reduce((acc, entity) => {
      const displayName = entity.entityDisplayName || 'Unknown';
      if (!acc[displayName]) {
        acc[displayName] = [];
      }
      acc[displayName].push(entity);
      return acc;
    }, {});

  // Create one accordion per unique entityDisplayName
  const content = Object.entries(entitiesByDisplayName).map(
    ([displayName, entities]) => {
      // Create all tables for this displayName
      const tableContent = entities.map((entity, idx) => (
        <div
          key={`table-wrapper-${displayName}`}
          data-testid={`table-wrapper-${displayName}-${idx}`}
          style={{ marginBottom: '1.5rem' }}
        >
          <AuditTable
            data={entity.entityFields}
            section={{}}
            headers={{
              loanAction: entity.entitySectionTitle ?? null,
              response: null,
            }}
          />
        </div>
      ));

      return (
        <AccordionContainer
          key={displayName}
          header={displayName}
          allowToggle
          defaultIndex={[-1]}
          layoutType={'summary1003AuditHistory'}
          marginTop="unset"
          accordionButtonProps={{
            padding: '1rem',
            accordionBgExpanded: 'blue.50',
          }}
          accordionPanelProps={{
            paddingLeft: '1rem',
            paddingRight: '1rem',
            padding: '1.5rem 1rem',
          }}
        >
          {tableContent}
        </AccordionContainer>
      );
    },
  );

  return <>{content}</>;
}

export default function AuditHistoryDynamicTabs(
  props: Readonly<AuditHistoryDynamicTabsProps>,
) {
  const { pageEnum, category } = useParams<AuditHistoryDynamicTabsParams>();
  const page = pageEnum || props.pageEnum;
  const isCaivrs = page === AuditHistoryPageEnum.VA_CAIVRS.toString();
  const {
    data: auditEntities,
    isLoading,
    isError,
  } = useGetAuditHistoryDynamicTabs(props.page, page, category);

  const [selectedTab, setSelectedTab] = useState(0);

  const hasAuditHistoryData =
    auditEntities?.find((e) => e.sectionTitle)?.entityFields.length > 0;

  // Group entities by sectionTitle
  const groupedEntities: Record<string, NormalizedAuditEntity[]> = (
    auditEntities || []
  ).reduce((acc, entity) => {
    acc[entity.sectionTitle] = acc[entity.sectionTitle] || [];
    acc[entity.sectionTitle].push(entity);
    return acc;
  }, {});

  // Unique tab names
  const tabNames = Object.keys(groupedEntities);

  return (
    <Layout
      pageEnum={page}
      category={category ?? props.page}
      customTitle={isCaivrs ? 'CAIVRS | Audit History' : undefined}
      isLoading={isLoading}
      isDataEmpty={!hasAuditHistoryData}
      isError={isError}
    >
      {auditEntities && (
        <>
          <Tabs
            sx={{
              marginBottom: '1rem',
              borderBottom: `1px solid ${palette.gray[300]}`,
            }}
            value={selectedTab}
            onChange={(_, newValue) => setSelectedTab(newValue)}
          >
            {auditEntities
              .filter(
                (item, index, self) =>
                  index ===
                  self.findIndex((t) => t.sectionTitle === item.sectionTitle),
              )
              .map(({ sectionTitle }) => (
                <Tab key={sectionTitle} label={sectionTitle} />
              ))}
          </Tabs>
          {tabNames.map((tabName, index) => (
            <TabPanel
              key={tabName}
              value={selectedTab}
              index={index}
              name={`auditHistory`}
              aria-label={`${props.page}-panel-${index}`}
              padding={0}
            >
              {TabPanelContent(auditEntities, index, tabNames, groupedEntities)}
            </TabPanel>
          ))}
        </>
      )}
    </Layout>
  );
}
